import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import {
  InstrumentForgeState,
  InstrumentForgeBlendConfig,
  BlendedProductionOutput
} from './types/instrumentForge.types';
import { generateBlendedOutput } from './utils/profileBlender';
import { useInstrumentProfiles } from '@hooks/useInstrumentProfiles';

interface InstrumentForgeContextType {
  state: InstrumentForgeState;
  selectProfile: (key: string | null) => void;
  setBlendMode: (mode: 'single' | 'blend') => void;
  setBlendConfig: (config: Partial<InstrumentForgeBlendConfig>) => void;
  swapProfiles: () => void;
  clearProfile: () => void;
}

const InstrumentForgeContext = createContext<InstrumentForgeContextType | null>(null);

const STORAGE_KEY = 'hookhouse_instrument_forge';

export const InstrumentForgeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data: instrumentProfiles } = useInstrumentProfiles();
  const [mode, setMode] = useState<'single' | 'blend'>('single');
  const [selectedProfileKey, setSelectedProfileKey] = useState<string | null>('classic-southern-rock');
  const [blendConfig, setBlendConfigState] = useState<InstrumentForgeBlendConfig>({
    profileAKey: 'classic-southern-rock',
    profileBKey: 'southern-sludge-doom',
    blendRatio: 50
  });

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.mode) setMode(parsed.mode);
        if (parsed.selectedProfileKey) setSelectedProfileKey(parsed.selectedProfileKey);
        if (parsed.blendConfig) setBlendConfigState(parsed.blendConfig);
      }
    } catch (e) {
      console.warn('Failed to load InstrumentForge state:', e);
    }
  }, []);

  // Reactive output generation - PATCHED
  const output = useMemo<BlendedProductionOutput | null>(() => {
    if (mode === 'single') {
      if (!selectedProfileKey) return null;
      return generateBlendedOutput({
        profileAKey: selectedProfileKey,
        profileBKey: null,
        blendRatio: 100
      }, instrumentProfiles);
    }

    if (mode === 'blend') {
      if (!blendConfig || !blendConfig.profileAKey) return null;
      return generateBlendedOutput(blendConfig, instrumentProfiles);
    }

    return null;
  }, [mode, selectedProfileKey, blendConfig, instrumentProfiles]);

  // Save to localStorage when state changes
  useEffect(() => {
    const stateToSave = { mode, selectedProfileKey, blendConfig };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [mode, selectedProfileKey, blendConfig]);

  const selectProfile = useCallback((key: string | null) => {
    setSelectedProfileKey(key);
    setMode('single');
  }, []);

  const setBlendMode = useCallback((newMode: 'single' | 'blend') => {
    setMode(newMode);
  }, []);

  const setBlendConfig = useCallback((updates: Partial<InstrumentForgeBlendConfig>) => {
    setBlendConfigState(prev => ({ ...prev, ...updates }));
  }, []);

  const swapProfiles = useCallback(() => {
    setBlendConfigState(prev => ({
      profileAKey: prev.profileBKey,
      profileBKey: prev.profileAKey,
      blendRatio: 100 - prev.blendRatio
    }));
  }, []);

  const clearProfile = useCallback(() => {
    setSelectedProfileKey(null);
    setMode('single');
  }, []);

  // Built state object for context
  const state: InstrumentForgeState = {
    mode,
    selectedProfileKey,
    blendConfig,
    output
  };

  return (
    <InstrumentForgeContext.Provider value={{
      state,
      selectProfile,
      setBlendMode,
      setBlendConfig,
      swapProfiles,
      clearProfile
    }}>
      {children}
    </InstrumentForgeContext.Provider>
  );
};

export const useInstrumentForge = () => {
  const context = useContext(InstrumentForgeContext);
  if (!context) throw new Error('useInstrumentForge must be used within InstrumentForgeProvider');
  return context;
};
